import { useTranslation } from 'react-i18next';
import type { PlatformCategoryCount } from '@/api/platformMetrics/platformMetricsApi.types';
import { ChartCard } from '@/components/common/charts/ChartCard';
import { DonutChart, type DonutSlice } from '@/components/common/charts/DonutChart';
import { usePlatformBreakdownQuery } from '@/hooks/usePlatformChartsQueries';
import { WORKSPACE_TYPE_COLORS, resolveSeriesColor } from '@/theme/chartPalette';

interface WorkspaceTypeChartProps {
  isEnabled: boolean;
}

export function WorkspaceTypeChart({ isEnabled }: WorkspaceTypeChartProps) {
  const { t } = useTranslation('admin');
  const breakdownQuery = usePlatformBreakdownQuery(isEnabled);
  const buckets = breakdownQuery.data === undefined ? [] : breakdownQuery.data.byType;
  const slices = buckets.map(function toSlice(bucket, index) {
    return toTypeSlice(bucket, index, t);
  });
  const total = slices.reduce(function addValue(sum, slice) {
    return sum + slice.value;
  }, 0);

  return (
    <ChartCard
      title={t('dashboard.charts.type.title')}
      iconName="Grid"
      isLoading={breakdownQuery.isLoading === true}
      isError={breakdownQuery.isError === true}
      onRetry={function retry() {
        breakdownQuery.refetch();
      }}
      isEmpty={total === 0}
      emptyLabel={t('dashboard.charts.type.empty')}
    >
      <DonutChart
        data={slices}
        accessibleLabel={t('dashboard.charts.type.title')}
        formatValue={formatWholeNumber}
      />
    </ChartCard>
  );
}

function toTypeSlice(
  bucket: PlatformCategoryCount,
  index: number,
  translate: (key: string, options: { defaultValue: string }) => string
): DonutSlice {
  const color = WORKSPACE_TYPE_COLORS[bucket.category];
  return {
    key: bucket.category,
    label: translate('dashboard.charts.type.' + bucket.category, { defaultValue: bucket.category }),
    value: bucket.count,
    color: color === undefined ? resolveSeriesColor(index) : color,
  };
}

function formatWholeNumber(value: number): string {
  return String(value);
}
